/** @jsxImportSource @emotion/react */

import { css } from '@emotion/react';

import { Theme as MaterialTheme, useTheme, Paper, Stack, TextField, Button, Container, CardHeader, Card, CardContent, Typography, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, IconButton, Backdrop, CircularProgress } from "@mui/material";
import Grid from '@mui/material/Unstable_Grid2';
import { useState, useEffect, Dispatch, SetStateAction, ChangeEvent, useRef, } from "react";

import { useAppBarHeight, useDebounce } from '../../../common/CustomHooks';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useParams } from 'react-router-dom';
import { Add, Delete, Edit } from '@mui/icons-material';
import DialogAddEditPaymentMethod from '../components/DialogAddEditPaymentMethods';
import OrganizationBusinessLogic from '../../../businessLogic/OrganizationBusinessLogic';
import { NumericFormatWhole } from '../../../common/CustomFunctions';
import { ICashRegisterGuiModel, ICashRegisterPutGuiModel, IPaymentMethodGuiModel } from '../../../model/IOrganizationGuiModel';

interface ITablePaymentMethods {
    paymentMethods: IPaymentMethodGuiModel[],
    paymentMethodsError: string[],
    organizationId: string,
    getCashRegisterPaymentMethods: () => void,
}

const TablePaymentMethods = (props: ITablePaymentMethods): JSX.Element => {

    const { t, i18n } = useTranslation();

    const [currentId, setCurrentId] = useState<number>();
    const [currentName, setCurrentName] = useState("");
    const [currentAccount, setCurrentAccount] = useState("");

    const [openDialog, setOpenDialog] = useState(false);
    const handleDialogOpen = (id: number, oldName: string, oldAccount: string) => {
        setCurrentId(id);
        setCurrentName(oldName);
        setCurrentAccount(oldAccount);
        setOpenDialog(true)
    }
    const handleDialogClose = () => {
        setOpenDialog(false)
    }

    return <TableContainer sx={{ maxHeight: "20rem" }}>
        <Table size="small" aria-label="tablePaymentMethods" stickyHeader sx={{ width: "100%",}}>
            <TableHead>
                <TableRow>
                    <TableCell>{t("basic.name")}</TableCell>
                    <TableCell>{t("basic.accountNumber")}</TableCell>
                    <TableCell></TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {props.paymentMethods.map(e => {
                    return <TableRow>
                        <TableCell>{e.name}</TableCell>
                        <TableCell>{e.accountNo}</TableCell>
                        <TableCell onClick={() => handleDialogOpen(e.id, e.name, e.accountNo)}><IconButton><Edit /></IconButton></TableCell>
                    </TableRow>
                }) }
            </TableBody>
        </Table>
        <DialogAddEditPaymentMethod open={openDialog}
            closeDialog={handleDialogClose}
            organizationId={props.organizationId}
            postOrPut={'put'}
            id={currentId}
            oldName={currentName}
            oldAccount={currentAccount}
            getCashRegisterPaymentMethods={props.getCashRegisterPaymentMethods} />
    </TableContainer>

}

const CashRegisterSettings = (): JSX.Element => {

    const { organizationId } = useParams();

    const [loader, setLoader] = useState(false);

    const theme = useTheme();

    const { t, i18n } = useTranslation();

    const [openDialog, setOpenDialog] = useState(false);
    const handleDialogOpen = () => {
        setOpenDialog(true)
    }
    const handleDialogClose = () => {
        setOpenDialog(false)
    }

    const [cashDifferenceAccountId, setCashDifferenceAccountId] = useState("");
    const [giftCardAccountId, setGiftCardAccountId] = useState("");
    const [withdrawnAccountId, setWithdrawnAccountId] = useState("");
    const [settingsError, setSettingsError] = useState<string[]>([]);

    const [paymentMethods, setPaymentMethods] = useState<IPaymentMethodGuiModel[]>([]);
    const [paymentMethodsError, setPaymentMetodsError] = useState<string[]>([]);

    //###############################################################################################################################################
    //########################################################API Calls and useEffects###############################################################
    useEffect(() => {
        let isMounted = true; //Avoid memory leaks. Async function running after dismount.
        //const service = new SourceDocumentService();

        getCashRegisterSettings(isMounted);
        getCashRegisterPaymentMethods(isMounted);

        return () => { isMounted = false };
    }, [organizationId]);

    //###############################################################################################################################################
    //###############################################################################################################################################
    const getCashRegisterSettings = async (isMounted: boolean) => {
        if (organizationId) {
            let logic = new OrganizationBusinessLogic();
            setLoader(true);
            const result = await logic.getCashRegisterSettings(organizationId);
            setLoader(false);
            if (result.hasErrors) {
                if (isMounted) {
                    toast(t("basic.errorData"));
                    setSettingsError(result.errors);
                    toast(t("basic.errorData") + result.errors.join(", "))
                }
            } else {
                setSettingsError([]);
                if (isMounted && result.value) {
                    setCashDifferenceAccountId(result.value.cashDifferenceAccountId);
                    setGiftCardAccountId(result.value.giftCardAccountId);
                    setWithdrawnAccountId(result.value.withdrawalAccountId)
                }

            }
        }
    }
    const getCashRegisterPaymentMethods = async (isMounted: boolean) => {
        if (organizationId) {
            let logic = new OrganizationBusinessLogic();
            setLoader(true);
            const result = await logic.getCashRegisterPaymentMethods(organizationId);
            setLoader(false);
            if (result.hasErrors) {
                if (isMounted) {
                    toast(t("basic.errorData"));
                    setPaymentMetodsError(result.errors);
                    toast(t("basic.errorData") + result.errors.join(", "))
                } 
            } else {
                if (isMounted) {
                    setPaymentMetodsError([]);
                    setPaymentMethods(result.value);
                }
            }
        }
    }
    const getPaymentMethodsAfterDialog = () => {
        getCashRegisterPaymentMethods(true);
    }
    const putCashRegisterSettings = async () => {
        if (organizationId) {
            if (parseInt(withdrawnAccountId) && parseInt(cashDifferenceAccountId) && parseInt(giftCardAccountId)) {
                let logic = new OrganizationBusinessLogic();

                let data: ICashRegisterGuiModel = {
                    withdrawalAccountId: withdrawnAccountId,
                    cashDifferenceAccountId: cashDifferenceAccountId,
                    giftCardAccountId: giftCardAccountId,
                }

                setLoader(true);
                const result = await logic.putCashRegisterSettings(organizationId, data);
                setLoader(false);
                if (result.hasErrors) {
                    toast(t("basic.errorDataSave"));
                } else {
                    ;
                    toast(t("basic.dataSaved"));

                }
            } else {
                toast(t("basic.onlyNumbersNoSave"));
            }
        }
    }
    //###############################################################################################################################################
    //###############################################################################################################################################
    const appBarHeight = useAppBarHeight();
    //########################################################################################
    //########################################################################################
    return <main>
        <Grid container direction="column" wrap="nowrap"
            sx={{
                padding: theme.spacing(2),
                height: {
                    xs: "calc(100vh - " + appBarHeight + "px )",
                    xl: "calc(100vh)"
                },
                //"& .MuiDataGrid-columnHeaders": { backgroundColor: theme.palette.info.main, color: "white" },
                //"& .MuiDataGrid-footerContainer": { backgroundColor: theme.palette.info.main, color: "white" }
            }}>
            {/*<Grid container spacing={2} padding={theme.spacing(2)}>*/}

            {/*</Grid>*/}
            {/*<Grid container paddingBottom={theme.spacing(2)}>*/}
            {/*    <Grid xs={12}>*/}
            {/*        <Paper sx={{*/}
            {/*            display: "flex",*/}
            {/*            justifyContent: "space-between",*/}
            {/*            alignItems: "center", padding: theme.spacing(2)*/}
            {/*        }}>*/}
            {/*            <Typography variant="h6">{t("sidemenu.cashRegisterSettings")}</Typography>*/}
            {/*        </Paper>*/}
            {/*    </Grid>*/}
            {/*</Grid>*/}
            <Grid container spacing={2}>
                <Grid xs={12} md={6}>
                    <Card sx={{ height: "100%" }}>
                        <CardHeader
                            title={t("basic.paymentMethods")}
                            titleTypographyProps={{ variant: "body1", fontWeight: "bold" }}
                            action={<Button onClick={handleDialogOpen} variant="contained" size="small" endIcon={<Add/> }>Add</Button>
                            }/>
                        <CardContent>
                            <TablePaymentMethods
                                organizationId={organizationId ? organizationId : ""}
                                paymentMethods={paymentMethods}
                                paymentMethodsError={paymentMethodsError}
                                getCashRegisterPaymentMethods={getPaymentMethodsAfterDialog} />
                        </CardContent>
                    </Card>
                </Grid>
                <Grid xs={12} md={6}>
                    <Card sx={{ height: "100%" }}>
                        <CardHeader title={t("basic.generalAccounts")} titleTypographyProps={{ variant: "body1", fontWeight: "bold" }} />
                        <CardContent>
                            <Stack direction="column" spacing={4}>
                                <TextField
                                    fullWidth
                                    //css={textBoxInputStyleActor(theme)}
                                    size="small"
                                    error={!parseInt(cashDifferenceAccountId)}
                                    variant="outlined"
                                    InputProps={{ inputComponent: NumericFormatWhole as any }}
                                    label={t("basic.cashDifferenceAccountId") + "."}
                                    color="warning"
                                    value={cashDifferenceAccountId}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => setCashDifferenceAccountId(e.target.value)} />
                                <TextField
                                    fullWidth
                                    //css={textBoxInputStyleActor(theme)}
                                    size="small"
                                    error={!parseInt(giftCardAccountId)}
                                    variant="outlined"
                                    InputProps={{ inputComponent: NumericFormatWhole as any }}
                                    label={t("basic.giftCardAccountId") + "."}
                                    color="warning"
                                    value={giftCardAccountId}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => setGiftCardAccountId(e.target.value)} />
                                <TextField
                                    fullWidth
                                    //css={textBoxInputStyleActor(theme)}
                                    size="small"
                                    error={!parseInt(withdrawnAccountId)}
                                    variant="outlined"
                                    InputProps={{ inputComponent: NumericFormatWhole as any }}
                                    label={t("basic.withdrawnAccountId") + "."}
                                    color="warning"
                                    value={withdrawnAccountId}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => setWithdrawnAccountId(e.target.value)} />
                                <Button onClick={putCashRegisterSettings} fullWidth variant="contained">{t("basic.save")}</Button>
                            </Stack>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Grid>
        <DialogAddEditPaymentMethod
            open={openDialog}
            closeDialog={handleDialogClose}
            organizationId={organizationId ? organizationId : ""}
            postOrPut={'post'}
            getCashRegisterPaymentMethods={getPaymentMethodsAfterDialog} />
        <Backdrop
            sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
            open={loader}
        >
            <CircularProgress color="inherit" />
        </Backdrop>
    </main>

}
export default CashRegisterSettings;